import {
  GENERATED_FILES_ARCHIVE_MAX_FILES,
  GENERATED_FILES_ARCHIVE_MAX_TOTAL_BYTES,
} from "@frontend-shared/constants";
import type {
  IGeneratedFile,
  TGeneratedFilesArchiveRequest,
} from "@frontend-shared/types";
import { EGeneratedFilesDownloadKind } from "@frontend-shared/types";
import {
  buildGeneratedFilesArchiveFileName,
  createDownloadProgressReporter,
  formatUploadLimitLabel,
  formatUploadSizeLabel,
  isAbortError,
  resolveGeneratedFilesArchiveErrorKey,
  resolveGeneratedFilesDownload,
} from "@frontend-shared/utils";
import { isAxiosError, isCancel } from "axios";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { chatApi, generatedFilesApi } from "@/api/services";
import { saveBlobAsFile } from "@/lib/save-blob-as-file";

import type {
  IBuildDownloadProgressLabelProps,
  IGeneratedFilesDownloadProgress,
  IRunDownloadProps,
  IUseGeneratedFilesDownloadProps,
  IUseGeneratedFilesDownloadResult,
} from "./use-generated-files-download.types";

const ACCESS_REVOKED_STATUS = 403;

const PROGRESS_TOAST_ICON = <Loader2 className="size-4 animate-spin" />;

const resolveResponseStatus = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

// NOTE: Asks for the native "Leave site?" prompt — the only thing a page can
// show on a refresh, a tab close or a typed URL; it cannot render its own
// dialog there. `returnValue` is still read by older Chromium.
const requestUnloadPrompt = (event: BeforeUnloadEvent): void => {
  event.preventDefault();
  Object.assign(event, { returnValue: "" });
};

// NOTE: NSAI-5312 — downloads what the user picked from their generated files:
// one file as itself, several as one ZIP. Both travel the way the single-file
// download always has (a blob with progress, saved with an anchor), plus the
// cancel a longer transfer needs. Shared by the AI Generated Files page, an
// agent run's files and a chat's "Download generated files" dialog, so all
// three report progress and word every failure identically.
export const useGeneratedFilesDownload = ({
  shouldShowProgressToast = false,
}: IUseGeneratedFilesDownloadProps = {}): IUseGeneratedFilesDownloadResult => {
  const { t } = useTranslation("common");
  const { t: tChat } = useTranslation("chat");
  const [progress, setProgress] =
    useState<IGeneratedFilesDownloadProgress | null>(null);
  // NOTE: Also the re-entrancy guard: a second click while one download is in
  // flight is ignored rather than starting a parallel one.
  const controllerRef = useRef<AbortController | null>(null);

  // NOTE: Leaving the page abandons an inline download instead of letting it
  // finish into a blob nobody will save. A toast download is not tied to the
  // component: its toast stays up across navigation, and closing it cancels.
  useEffect(
    () => (): void => {
      if (!shouldShowProgressToast) {
        controllerRef.current?.abort();
      }
    },
    [shouldShowProgressToast],
  );

  const buildProgressLabel = useCallback(
    ({ progress: current }: IBuildDownloadProgressLabelProps): string => {
      if (current.percent !== undefined) {
        return t("generated_files.archive.downloading", {
          percent: current.percent,
        });
      }

      if (current.downloadedBytes > 0) {
        return t("generated_files.archive.downloading_size", {
          size: formatUploadSizeLabel({
            sizeInBytes: current.downloadedBytes,
          }),
        });
      }

      return current.kind === EGeneratedFilesDownloadKind.ARCHIVE
        ? t("generated_files.archive.preparing")
        : t("generated_files.download_starting");
    },
    [t],
  );

  const cancelDownload = useCallback((): void => {
    controllerRef.current?.abort();
  }, []);

  const runDownload = useCallback(
    async ({
      kind,
      fileName,
      fetchBlob,
      resolveErrorMessage,
    }: IRunDownloadProps): Promise<void> => {
      if (controllerRef.current !== null) {
        return;
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      const initialProgress: IGeneratedFilesDownloadProgress = {
        kind,
        downloadedBytes: 0,
      };
      setProgress(initialProgress);

      // NOTE: A plain toast rather than toast.loading, and onDismiss as the
      // cancel. Two separate facts about sonner (1.7) make that work:
      // 1. The toast's ✕ is this download's Cancel. On a loading toast sonner
      //    disables the ✕ (it does nothing), so that toast could not be
      //    cancelled; on a plain one the ✕, or a swipe, runs onDismiss.
      // 2. A finished download is not reported as cancelled. The `finally`
      //    below closes the toast with toast.dismiss(), and sonner runs
      //    onDismiss only when the user closes a toast, never on a dismiss
      //    from code.
      const toastOptions = {
        icon: PROGRESS_TOAST_ICON,
        duration: Infinity,
        onDismiss: (): void => controller.abort(),
      };
      const toastId = shouldShowProgressToast
        ? toast(buildProgressLabel({ progress: initialProgress }), toastOptions)
        : undefined;

      // NOTE: A toast download outlives the page it started on, so in-app
      // navigation and back cannot lose it; only unloading the tab can. It is
      // guarded for exactly its own lifetime — added here, removed in the
      // `finally` below — rather than by an effect, so it holds even when the
      // component that started it unmounts. Each download registers its own
      // listener: two chats downloading at once must not share one, or the
      // first to finish would unguard the other. An inline download is
      // guarded by the page's UploadNavigationGuard instead.
      const handleBeforeUnload = (event: BeforeUnloadEvent): void =>
        requestUnloadPrompt(event);

      if (shouldShowProgressToast) {
        window.addEventListener("beforeunload", handleBeforeUnload);
      }

      const reportProgress = createDownloadProgressReporter({
        onProgress: (report): void => {
          const next: IGeneratedFilesDownloadProgress = {
            kind,
            downloadedBytes: report.downloadedBytes,
            ...(report.progress !== undefined && { percent: report.progress }),
          };
          setProgress(next);

          // NOTE: Updating a toast the user already closed would bring it
          // back, since sonner re-creates an id it no longer holds.
          if (toastId !== undefined && !controller.signal.aborted) {
            toast(buildProgressLabel({ progress: next }), {
              ...toastOptions,
              id: toastId,
            });
          }
        },
      });

      try {
        const blob = await fetchBlob({
          signal: controller.signal,
          onProgress: (event) =>
            reportProgress({
              loaded: event.loaded,
              ...(event.total !== undefined && { total: event.total }),
            }),
        });

        saveBlobAsFile({ blob, fileName });
      } catch (error) {
        // NOTE: The user's own Cancel is not a failure; axios names an aborted
        // request CanceledError, not AbortError, hence both checks.
        if (!isCancel(error) && !isAbortError(error)) {
          toast.error(resolveErrorMessage(error));
        }
      } finally {
        if (toastId !== undefined) {
          toast.dismiss(toastId);
        }
        window.removeEventListener("beforeunload", handleBeforeUnload);
        controllerRef.current = null;
        setProgress(null);
      }
    },
    [buildProgressLabel, shouldShowProgressToast],
  );

  const downloadArchive = useCallback(
    (request: TGeneratedFilesArchiveRequest): Promise<void> =>
      runDownload({
        kind: EGeneratedFilesDownloadKind.ARCHIVE,
        fileName: buildGeneratedFilesArchiveFileName({ date: new Date() }),
        fetchBlob: ({ signal, onProgress }) =>
          generatedFilesApi.downloadArchive({ request, signal, onProgress }),
        resolveErrorMessage: (error) =>
          t(
            resolveGeneratedFilesArchiveErrorKey({
              status: resolveResponseStatus(error),
              scope: request.scope,
            }),
            {
              maxFiles: GENERATED_FILES_ARCHIVE_MAX_FILES,
              maxSize: formatUploadLimitLabel({
                sizeInBytes: GENERATED_FILES_ARCHIVE_MAX_TOTAL_BYTES,
              }),
            },
          ),
      }),
    [runDownload, t],
  );

  const downloadFile = useCallback(
    (file: IGeneratedFile): Promise<void> =>
      runDownload({
        kind: EGeneratedFilesDownloadKind.FILE,
        fileName: file.name,
        fetchBlob: ({ signal, onProgress }) =>
          chatApi.downloadFile({ fileUuid: file.uuid, signal, onProgress }),
        // NOTE: Worded exactly like the row's own Download button
        // (DownloadFileButton), since it is the same download.
        resolveErrorMessage: (error) =>
          resolveResponseStatus(error) === ACCESS_REVOKED_STATUS
            ? tChat("knowledge_sources.access_revoked")
            : tChat("knowledge_sources.download_failed"),
      }),
    [runDownload, tChat],
  );

  const downloadFiles = useCallback(
    async (files: readonly IGeneratedFile[]): Promise<void> => {
      const download = resolveGeneratedFilesDownload({ files });

      if (download === null) {
        return;
      }

      if (download.kind === EGeneratedFilesDownloadKind.FILE) {
        await downloadFile(download.file);
        return;
      }

      await downloadArchive(download.request);
    },
    [downloadArchive, downloadFile],
  );

  const progressLabel = useMemo(
    () => (progress === null ? null : buildProgressLabel({ progress })),
    [buildProgressLabel, progress],
  );

  return {
    downloadFiles,
    downloadArchive,
    cancelDownload,
    isDownloading: progress !== null,
    progress,
    progressLabel,
  };
};
